// PAGE SCHEDULING
// Mirrors PageScheduleSchema / app/v2/schedule.js in jupiter.
// pageSchedules[i] belongs to children[i] (same order, like pageDurations).
// Times are wall-clock 'HH:mm' in Europe/Vienna, validFrom / validUntil are absolute Dates.
// The backend allows '24:00' as timeslot end, <input type="time"> can't show it,
// so in the editor midnight is '00:00' and gets converted back on save.

export const DAYS = [
  { key: 'monday',    label: 'Montag',     short: 'Mo' },
  { key: 'tuesday',   label: 'Dienstag',   short: 'Di' },
  { key: 'wednesday', label: 'Mittwoch',   short: 'Mi' },
  { key: 'thursday',  label: 'Donnerstag', short: 'Do' },
  { key: 'friday',    label: 'Freitag',    short: 'Fr' },
  { key: 'saturday',  label: 'Samstag',    short: 'Sa' },
  { key: 'sunday',    label: 'Sonntag',    short: 'So' }
];

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

export function emptySchedule() {
  const days = {};
  DAYS.forEach(({ key }) => { days[key] = { enabled: false, timeslots: [] }; });
  return { validFrom: null, validUntil: null, days };
}

// Fills missing fields of a schedule coming from the api
export function normalizeSchedule(schedule) {
  const result = emptySchedule();
  if (!schedule) { return result; }
  result.validFrom  = schedule.validFrom  || null;
  result.validUntil = schedule.validUntil || null;
  const days = schedule.days || {};
  DAYS.forEach(({ key }) => {
    const day = days[key] || {};
    result.days[key] = {
      enabled   : !!day.enabled,
      timeslots : (day.timeslots || []).map((slot) => ({
        start : slot.start,
        end   : slot.end === '24:00' ? '00:00' : slot.end
      }))
    };
  });
  return result;
}

// Aligns schedules with the number of pages in the channel
export function normalizeSchedules(schedules, length) {
  const result = [];
  for (let i = 0; i < length; i++) {
    result.push(normalizeSchedule((schedules || [])[i]));
  }
  return result;
}

// Returns a list of error messages, empty if valid
export function validateSchedule(schedule) {
  const errors = [];
  if (schedule.validFrom && schedule.validUntil &&
      new Date(schedule.validFrom) >= new Date(schedule.validUntil)) {
    errors.push('"Gültig bis" muss nach "Gültig ab" liegen');
  }
  DAYS.forEach(({ key, label }) => {
    const day = schedule.days[key];
    if (!day.enabled) { return; }
    day.timeslots.forEach((slot) => {
      if (!HHMM.test(slot.start || '') || !HHMM.test(slot.end || '')) {
        errors.push(`${label}: Zeitfenster unvollständig`);
      } else if (slot.end !== '00:00' && slot.start >= slot.end) {
        errors.push(`${label}: Ende (${slot.end}) muss nach Beginn (${slot.start}) liegen`);
      }
    });
  });
  return errors;
}

// Prepares a schedule for the api
export function serializeSchedule(schedule) {
  const result = normalizeSchedule(schedule);
  DAYS.forEach(({ key }) => {
    result.days[key].timeslots.forEach((slot) => {
      if (slot.end === '00:00') { slot.end = '24:00'; }
    });
  });
  return result;
}

export function isScheduleExpired(schedule, now = new Date()) {
  return !!(schedule && schedule.validUntil && now > new Date(schedule.validUntil));
}

export function isSchedulePending(schedule, now = new Date()) {
  return !!(schedule && schedule.validFrom && now < new Date(schedule.validFrom));
}

// Same as isPageVisible in jupiter (app/v2/schedule.js), but works on the
// editor format (timeslot end '00:00' = midnight)
const zonedFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone : 'Europe/Vienna',
  weekday  : 'long',
  hour     : '2-digit',
  minute   : '2-digit',
  hourCycle: 'h23'
});

function getZonedNow(date) {
  const parts = {};
  zonedFormatter.formatToParts(date).forEach((part) => { parts[part.type] = part.value; });
  const hour = parts.hour === '24' ? '00' : parts.hour;
  return { day: parts.weekday.toLowerCase(), time: hour + ':' + parts.minute };
}

export function isScheduleActive(schedule, now = new Date()) {
  if (!schedule) { return true; }
  if (isSchedulePending(schedule, now) || isScheduleExpired(schedule, now)) { return false; }

  const anyDayEnabled = DAYS.some(({ key }) => schedule.days[key].enabled);
  if (!anyDayEnabled) { return true; }

  const { day, time } = getZonedNow(now);
  const today = schedule.days[day];
  if (!today || !today.enabled) { return false; }
  if (today.timeslots.length === 0) { return true; }

  return today.timeslots.some((slot) => {
    const end = slot.end === '00:00' ? '24:00' : slot.end;
    return slot.start <= time && time < end;
  });
}

function formatDate(date) {
  return new Date(date).toLocaleString('de-AT', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
}

// Short text for the channel editor, null if the page is always shown
export function scheduleSummary(schedule) {
  if (!schedule) { return null; }
  const parts = [];
  const enabledDays = DAYS.filter(({ key }) => schedule.days[key].enabled);
  if (enabledDays.length > 0) {
    parts.push(enabledDays.map((d) => {
      const slots = schedule.days[d.key].timeslots;
      if (slots.length === 0) { return d.short; }
      return d.short + ' ' + slots.map((s) => `${s.start}–${s.end === '00:00' ? '24:00' : s.end}`).join(', ');
    }).join(' · '));
  }
  const range = [];
  if (schedule.validFrom)  { range.push('ab '  + formatDate(schedule.validFrom)); }
  if (schedule.validUntil) { range.push('bis ' + formatDate(schedule.validUntil)); }
  if (range.length > 0)    { parts.push(range.join(' ')); }
  return parts.length > 0 ? parts.join(' | ') : null;
}
